import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Loader2, Plus, AlertTriangle, Clock, CheckCircle2, Send,
  Building2, FileUp,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const EXT_STATUS = {
  RECEIVED: { label: "Ricevuto", cls: "bg-slate-100 text-slate-700" },
  ANALYZING: { label: "In analisi", cls: "bg-slate-100 text-slate-700" },
  READY_FOR_COMPLETION: { label: "Pronto da compilare", cls: "bg-sky-100 text-sky-800" },
  IN_PROGRESS: { label: "In compilazione", cls: "bg-sky-100 text-sky-800" },
  SUBMITTED_BY_COMPANY: { label: "Inviato al consulente", cls: "bg-indigo-100 text-indigo-800" },
  UNDER_CONSULTANT_REVIEW: { label: "In revisione", cls: "bg-amber-100 text-amber-800" },
  INTEGRATION_REQUESTED: { label: "Integrazioni richieste", cls: "bg-red-100 text-red-800" },
  READY_FOR_APPROVAL: { label: "Pronto per approvazione", cls: "bg-amber-100 text-amber-800" },
  APPROVED: { label: "Approvato", cls: "bg-green-100 text-green-800" },
  EXPORTED: { label: "Esportato", cls: "bg-green-700 text-white" },
  ARCHIVED: { label: "Archiviato", cls: "bg-slate-100 text-slate-500" },
};
const DEADLINE = {
  ON_TRACK: { label: "Nei tempi", cls: "text-green-700" },
  DUE_SOON: { label: "In scadenza", cls: "text-amber-700" },
  OVERDUE: { label: "Scaduto", cls: "text-red-700" },
  COMPLETED: { label: "Completato", cls: "text-green-700" },
};
const TPL_STATUS = {
  DRAFT: "Bozza", IMPORTED: "Importato", MAPPING_REQUIRED: "Mapping richiesto",
  UNDER_REVIEW: "In revisione", ACTIVE: "Attivo", DEPRECATED: "Deprecato",
  ARCHIVED: "Archiviato",
};

export default function ExternalQuestionnaires() {
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const isConsultant = can("externalq:review");
  const [instances, setInstances] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [assignTpl, setAssignTpl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) {
      setInstances([]);
      return;
    }
    setError(null);
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/external-questionnaires`);
      setInstances(data.instances);
    } catch (e) {
      // Log tecnico diagnostico (senza PII); l'utente vede stato errore + retry.
      console.error("[ext-questionnaires] load failed:", e?.response?.status, e?.code, e?.message);
      setError(formatApiError(e));
      setInstances([]);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isConsultant) return;
    api.get("/external/templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get("/external/providers").then((r) => setProviders(r.data.providers)).catch(() => {});
  }, [isConsultant]);

  const assign = async () => {
    if (!assignTpl) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/external-questionnaires`,
        { template_id: assignTpl, due_date: dueDate || null,
          recipient_name: recipient || null });
      toast.success(`Questionario assegnato: ${data.stats?.prefill_percentage ?? 0}% già compilato da ESG-PMI`);
      setShowAssign(false);
      navigate(`/questionari-esterni/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const importFile = async (file, providerId, name, code) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("provider_id", providerId);
      form.append("name", name);
      form.append("code", code);
      const { data } = await api.post("/external/templates/import", form);
      toast.success(`${data.questions_count} domande importate — completa la review dei mapping`);
      setShowImport(false);
      navigate(`/questionari-esterni/template/${data.template.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ext-questionnaires-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            Questionari esterni
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isConsultant
              ? "Questionari di banche, clienti e capofiliera: assegna, revisiona, approva ed esporta."
              : "Le richieste ESG di banche e clienti: gran parte delle risposte è già nel tuo patrimonio ESG. Completa solo ciò che manca."}
          </p>
        </div>
        {isConsultant && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={() => setShowImport(true)} data-testid="import-template-btn">
              <FileUp className="w-3 h-3 mr-1" aria-hidden="true" />Importa questionario
            </Button>
            <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
              onClick={() => setShowAssign(true)} data-testid="assign-questionnaire-btn">
              <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Assegna questionario
            </Button>
          </div>
        )}
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center" data-testid="extq-error">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <Button size="sm" variant="outline" onClick={load} data-testid="extq-retry-btn">
              Riprova
            </Button>
          </CardContent>
        </Card>
      ) : !instances ? (
        <p className="text-sm text-slate-500" data-testid="extq-loading">Caricamento…</p>
      ) : instances.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center" data-testid="extq-empty">
            <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-slate-600">
              {isConsultant
                ? "Nessun questionario assegnato a questa azienda per il periodo."
                : "Nessun questionario da compilare al momento."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="extq-list">
          {instances.map((i) => {
            const st = EXT_STATUS[i.status] || EXT_STATUS.RECEIVED;
            const dl = DEADLINE[i.deadline_status] || DEADLINE.ON_TRACK;
            const stats = i.stats || {};
            return (
              <Card key={i.id} className="border-slate-200 hover:border-green-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/questionari-esterni/${i.id}`)}
                data-testid={`extq-row-${i.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="p-2 rounded-lg bg-slate-100 text-slate-600">
                      <Building2 className="w-4 h-4" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-900">
                          {i.recipient_name || i.provider_name || i.template_code}
                        </p>
                        <Badge className={st.cls} data-testid={`extq-status-${i.id}`}>{st.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {i.provider_name}{i.due_date && ` · Scadenza ${new Date(i.due_date).toLocaleDateString("it-IT")}`}
                        {" · "}<span className={dl.cls}>{dl.label}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-center">
                      <div>
                        <p className="text-lg font-extrabold text-green-800"
                          data-testid={`extq-prefill-${i.id}`}>{stats.prefill_percentage ?? 0}%</p>
                        <p className="text-[10px] text-slate-500">già disponibile</p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-red-700"
                          data-testid={`extq-missing-${i.id}`}>{stats.missing ?? 0}</p>
                        <p className="text-[10px] text-slate-500">da completare</p>
                      </div>
                      <div>
                        <p className="text-lg font-extrabold text-amber-700"
                          data-testid={`extq-toverify-${i.id}`}>{stats.to_verify ?? 0}</p>
                        <p className="text-[10px] text-slate-500">da verificare</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {isConsultant && templates.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Libreria template</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="template-library">
            {templates.map((t) => (
              <div key={t.id}
                className="flex items-center gap-3 rounded-md border border-slate-100 p-3 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/questionari-esterni/template/${t.id}`)}
                data-testid={`template-row-${t.code}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">
                    {t.code} · {t.version} · {t.questions_count ?? 0} domande · scope {t.scope}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs"
                  data-testid={`template-status-${t.code}`}>{TPL_STATUS[t.status] || t.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog" aria-modal="true" data-testid="assign-dialog">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2"><CardTitle className="text-base">Assegna questionario</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Select value={assignTpl} onValueChange={setAssignTpl}>
                <SelectTrigger data-testid="assign-template-select" aria-label="Template">
                  <SelectValue placeholder="Seleziona template attivo" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => t.status === "ACTIVE").map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.version})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="text" value={recipient} onChange={(e) => setRecipient(e.target.value)}
                placeholder="Destinatario (es. Banca Demo — Filiale Milano)" maxLength={200}
                className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
                data-testid="assign-recipient-input" aria-label="Destinatario" />
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
                data-testid="assign-due-date" aria-label="Scadenza" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs"
                  onClick={() => setShowAssign(false)} data-testid="assign-cancel">Annulla</Button>
                <Button size="sm" className="h-8 text-xs bg-green-700 text-white"
                  disabled={!assignTpl || busy} onClick={assign} data-testid="assign-confirm">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Assegna e precompila"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showImport && (
        <ImportDialog providers={providers} busy={busy} onImport={importFile}
          onClose={() => setShowImport(false)} />
      )}
    </div>
  );
}

function ImportDialog({ providers, busy, onImport, onClose }) {
  const [file, setFile] = useState(null);
  const [providerId, setProviderId] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" data-testid="import-dialog">
      <Card className="w-full max-w-md">
        <CardHeader className="pb-2"><CardTitle className="text-base">Importa questionario</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            XLSX, CSV o JSON strutturati; PDF/DOCX analizzati dall'AI. Il template resterà
            in revisione finché non verifichi i mapping.
          </p>
          <Select value={providerId} onValueChange={setProviderId}>
            <SelectTrigger data-testid="import-provider-select" aria-label="Provider">
              <SelectValue placeholder="Provider (banca/cliente)" />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Nome template" maxLength={200}
            className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
            data-testid="import-name-input" aria-label="Nome template" />
          <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, "_"))}
            placeholder="Codice (es. BANCA_X_ESG_2026)" maxLength={60}
            className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm font-mono"
            data-testid="import-code-input" aria-label="Codice template" />
          <input type="file" accept=".xlsx,.csv,.json,.pdf,.docx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-xs" data-testid="import-file-input" aria-label="File questionario" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={onClose} data-testid="import-cancel">Annulla</Button>
            <Button size="sm" className="h-8 text-xs bg-green-700 text-white"
              disabled={!file || !providerId || !name || !code || busy}
              onClick={() => onImport(file, providerId, name, code)}
              data-testid="import-confirm">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Importa"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
